<template>
  <div class="footer-cn" :class="{ 'footer-cn--retractable': retractable }" data-buried="Footer">
    <div class="footer-cn__content">
      <div class="footer-cn__left">
        <div class="footer-cn__logo">
          <Resource
            class="footer-cn__logo-image"
            :src="LogoImg"
            alt="Rendora Logo"
            fetchpriority="low"
            @click="gotoHome"
          />
        </div>
      </div>
      <div class="footer-cn__links">
        <div class="footer-cn__column">
          <div class="footer-cn__column-title">产品与资源</div>
          <ul class="footer-cn__list footer-cn__list--product">
            <li v-for="item in productLinks" :key="item.name" class="footer-cn__list-item">
              <a class="footer-cn__link" :href="item.url" :target="item.target">{{ item.name }}</a>
            </li>
          </ul>
        </div>
        <div class="footer-cn__column">
          <div class="footer-cn__column-title">应用场景</div>
          <ul class="footer-cn__list">
            <li v-for="item in useCaseLinks" :key="item.name" class="footer-cn__list-item">
              <a class="footer-cn__link" :href="item.url" :target="item.target">{{ item.name }}</a>
            </li>
          </ul>
        </div>
        <div class="footer-cn__column">
          <div class="footer-cn__column-title">魔珐学院</div>
          <ul class="footer-cn__list">
            <li v-for="item in campusLinks" :key="item.name" class="footer-cn__list-item">
              <a class="footer-cn__link" :href="item.url" :target="item.target">{{ item.name }}</a>
            </li>
          </ul>
        </div>
        <div class="footer-cn__column">
          <div class="footer-cn__column-title">支持与服务</div>
          <ul class="footer-cn__list">
            <li v-for="item in protocolLinks" :key="item.name" class="footer-cn__list-item">
              <a class="footer-cn__link" :href="item.url" :target="item.target">{{ item.name }}</a>
            </li>
          </ul>
        </div>
        <div class="footer-cn__column footer-cn__column--friend">
          <div class="footer-cn__column-title">友情链接</div>
          <ul class="footer-cn__list">
            <li v-for="item in friendLinks" :key="item.name" class="footer-cn__list-item">
              <a class="footer-cn__link" :href="item.url" :target="item.target">{{ item.name }}</a>
            </li>
          </ul>
        </div>
        <div class="footer-cn__column footer-cn__column--company">
          <div class="footer-cn__column-title">联系我们</div>
          <ul class="footer-cn__list">
            <li class="footer-cn__list-item footer-cn__contact-item">
              <iconpark-icon name="mail" size="16"></iconpark-icon>
              意见反馈：youyan@xmov.ai
            </li>
            <li class="footer-cn__list-item footer-cn__contact-item">
              <iconpark-icon name="phone" size="16"></iconpark-icon>
              咨询热线：18201965780
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div class="footer-cn__bottom-logo">
      <Resource
        class="footer-cn__logo-image"
        :src="LogoImg"
        alt="Rendora Logo"
        fetchpriority="low"
        @click="gotoHome"
      />
    </div>
    <div class="footer-cn__agreements">
      <div class="footer-cn__copyright pc-only">©上海墨舞科技有限公司 All Right Reserved</div>
      <template v-for="(item, index) in footerLinks" :key="item.name">
        <div class="footer-cn__separator">
          <div class="footer-cn__dot"></div>
        </div>
        <div class="footer-cn__icp-serial">
          <div class="footer-cn__icp-content">
            <img
              v-if="item.name === '沪公网安备31010402333897号'"
              :src="getImageUrl('icp.webp', 'w_48')"
              class="footer-cn__icp-icon"
              alt="icp"
            />
            <a class="footer-cn__icp-link" :href="item.url" :target="item.target">
              {{ item.name }}
            </a>
          </div>
        </div>
      </template>
      <div class="footer-cn__copyright mobile-only">©上海墨舞科技有限公司 All Right Reserved</div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import Resource from '~/components/resource/index';
import { getImageUrl, getRedirectLink } from '~/utils/landing';
import { getFeUrl } from '~/utils/url';

const props = withDefaults(
  defineProps<{
    retractable?: boolean;
  }>(),
  {
    retractable: true,
  },
);

const LogoImg = getFeUrl('/youyan-platform/main/footer/logo.png');

const is3D = ref(false);
const request = useRequestURL();
is3D.value = request?.hostname !== 'youyan.xyz';

const footerLinks = [
  {
    name: '营业执照',
    url: getFeUrl('/certificate.pdf'),
    target: '_blank',
  },
  {
    name: '沪公网安备31010402333897号',
    url: 'http://www.beian.gov.cn/portal/registerSystemInfo?recordcode=31010402333897',
    target: '_blank',
  },
  {
    name: '网信算备310104618711101240015号',
    url: 'https://beian.cac.gov.cn/#/index',
    target: '_blank',
  },
  {
    name: '沪ICP备2023029693号-3',
    url: 'https://beian.miit.gov.cn/#/Integrated/index',
    target: '_blank',
  },
  {
    name: '宙语Shanghai-ZhouYu-202511240101',
    url: 'https://www.cac.gov.cn/2024-04/02/c_1713729983803145.htm',
    target: '_blank',
  },
];

const productLinks = [
  {
    name: '首页',
    url: '/',
    target: '_self',
  },
  {
    name: '3D数字人',
    url: '/3d-ai-avatar',
    target: '_self',
  },
  {
    name: '数字人广场',
    url: '/characters',
    target: '_self',
  },
  {
    name: 'AI视频生成',
    url: '/ai-avatar-video-generation',
    target: '_self',
  },
  {
    name: '作品中心',
    url: '/template',
    target: '_self',
  },
  {
    name: 'AI语音生成',
    url: '/ai-voice',
    target: '_self',
  },
  {
    name: 'PPT讲解智能体',
    url: '/ppt-speak',
    target: '_self',
  },
  {
    name: 'PPT生成视频',
    url: '/ppt-or-pdf-to-video',
    target: '_self',
  },
  {
    name: 'AI生成PPT',
    url: '/ai-presentation-generator',
    target: '_self',
  },
  {
    name: '视频场景',
    url: '/scene',
    target: '_self',
  },

  {
    name: 'API',
    url: '/api',
    target: '_self',
  },
  {
    name: 'AI互动数字人',
    url: 'https://xingyun3d.com/',
    target: '_blank',
  },
];

const scenariosTags = useScenariosTags();

const useCaseLinks = computed(() => {
  return scenariosTags.value.map(tag => ({
    name: tag.tag_name,
    url: `/scenarios/${tag.tag_outer_id}`,
    target: '_self',
  }));
});

const friendLinks = [
  {
    name: '图司机',
    url: 'https://www.tusij.com/',
    target: '_blank',
  },
  {
    name: '135AI图文排版',
    url: 'https://www.135editor.com/ai_editor/?track_id=32&hmmd=youyan',
    target: '_blank',
  },
  {
    name: '新片场',
    url: 'https://stock.xinpianchang.com/',
    target: '_blank',
  },
];

const protocolLinks = [
  {
    name: '付费订阅',
    url: '/subscribe',
    target: '_self',
  },
  {
    name: '帮助中心',
    url: '/help-center',
    target: '_self',
  },
  {
    name: '快速上手',
    url: '/help-center/41-155.html',
    target: '_self',
  },
  {
    name: '使用指南',
    url: '/help-center/42-137.html',
    target: '_self',
  },
  {
    name: '常见问题',
    url: '/help-center/43-146.html',
    target: '_self',
  },
  {
    name: '最新动态',
    url: '/help-center/44-152.html',
    target: '_self',
  },
  {
    name: '企业服务',
    url: '/help-center/45-205.html',
    target: '_self',
  },
  {
    name: '安全合规',
    url: '/help-center/44-183.html',
    target: '_self',
  },
  {
    name: '法律声明',
    url: 'https://frontend-protocol.xmov.ai/youyan/legal-notice',
    target: '_blank',
  },
  {
    name: '用户协议',
    url: 'https://frontend-protocol.xmov.ai/youyan-v3/user',
    target: '_blank',
  },
  {
    name: '隐私政策',
    url: 'https://frontend-protocol.xmov.ai/youyan-v3/privacy',
    target: '_blank',
  },
];

const campusLinks = [
  {
    name: '了解有言',
    url: '/category/what-is-youyan-32',
    target: '_self',
  },
  {
    name: '行业干货',
    url: '/category/industry-examples-17',
    target: '_self',
  },
  {
    name: '客户案例',
    url: '/customer-stories',
    target: '_self',
  },
  {
    name: '直播分享',
    url: '/category/live-streaming-case-examples-26',
    target: '_self',
  },
  {
    name: '操作教程',
    url: '/tutorial',
    target: '_self',
  },
];

function gotoHome() {
  window.location.href = getRedirectLink(window.location.origin, true);
}
</script>

<style lang="scss" scoped src="./pc.scss" />
<style lang="scss" scoped src="./mobile.scss" />
